/**
 * AsTooltip Component
 * 
 * Generally used as a child of other components, like `as-icon-button`, 
 * but placed as a sibling next to the actual content.
 * Use events to show and hide the tooltip.
 * 
 * Features:
 * - Supports various placements including top, bottom, left, right, and corner alignments.
 * - Automatically adjusts its position based on window resize and scroll events.
 * - Allows customization through CSS variables for colors and border styling.
 * - Displays fallback text if no content is provided in the slot.
 * 
 * CSS Variables:
 * - `--tooltip-bg-col`: Background color of the tooltip (default: rgba(0, 0, 0, 0.8)).
 * - `--tooltip-text-col`: Text color of the tooltip (default: white).
 * - `--tooltip-border-col`: Border color of the tooltip (default: same as background color).
 * - `--tooltip-padding`: Padding inside the tooltip (default: 0.5rem 0.75rem).
 * - `--tooltip-border-radius`: Border radius of the tooltip (default: 0.25rem).
 * - `--tooltip-arrow-size`: Size of the tooltip arrow (default: 6px).
 * - `--tooltip-max-width`: Maximum width of the tooltip (default: 250px).
 * 
 * Observed Attributes:
 * - `show`: Controls the visibility of the tooltip. If present, the tooltip is shown.
 * - `placement`: Determines the position of the tooltip relative to its parent. 
 *   Possible values: 'top', 'bottom', 'left', 'right', 'top-left', 'top-right', 'bottom-left', 'bottom-right'.
 * - `no-arrow`: When present, hides the tooltip arrow.
 * 
 * Methods:
 * - connectedCallback(): Invoked when the element is inserted into the DOM. Sets up event listeners and initializes state.
 * - disconnectedCallback(): Invoked when the element is removed from the DOM. Cleans up event listeners and observers.
 * - attributeChangedCallback(name, oldValue, newValue): Handles changes to observed attributes to update visibility and placement.
 * - updateVisibility(): Updates the tooltip's visibility based on the 'show' attribute.
 * - updatePlacement(): Updates the tooltip's position based on the 'placement' attribute.
 * - updateArrowVisibility(): Updates the tooltip arrow visibility based on the 'no-arrow' attribute.
 * - handleSlotChange(event): Handles changes to the slot content to ensure fallback text is shown if necessary.
 * - handleWindowResize(): Adjusts the tooltip's position on window resize events.
 * - handleScroll(): Adjusts the tooltip's position on scroll events.
 * 
 * Events:
 * - 'slotchange': Triggered when the content of the slot changes, ensuring the tooltip content is updated accordingly.
 * 
 * Usage:
 * ```
 * <as-tooltip show placement="top">
 *   Tooltip content goes here, if overriden. Ideally dict-key and default-text are provided to populate an as-text component.
 * </as-tooltip>
 * ```
 */
import { devlog } from '../../logic/utils.ts';
import '../text-and-lang/as-text.ts';
import styleText from './as-tooltip.css?raw';

export class AsTooltip extends HTMLElement {
  private static readonly sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();
  private fallbackText = '...';
  private resizeObserver: ResizeObserver | null = null;
  private mutationObserver: MutationObserver | null = null;
  private tooltipParent: HTMLElement | null = null;
  private flashTimeout?: number;
  private _isElementConnected = false;

  /**
   * Define attributes to observe for changes
   */
  static get observedAttributes(): string[] {
    return ['show', 'placement', 'dict-key', 'default-text', 'no-arrow', 'flash', 'flash-duration', 'flash-delay'];
  }
    
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    
    this.shadowRoot!.adoptedStyleSheets = [
      ...this.shadowRoot!.adoptedStyleSheets,
      AsTooltip.sheet
    ];

    // Generate content based on whether we have a dict-key or regular content
    const dictKey = this.getAttribute('dict-key');
    const defaultText = this.getAttribute('default-text') ?? '';
    
    this.shadowRoot!.innerHTML = /*html*/`
      ${this.getAttribute('no-arrow')?'':`<div class="tooltip-arrow"></div>`}
      <span class="tooltip-text">
        ${dictKey 
          ? `<as-text dict-key="${dictKey}">${defaultText}</as-text>` 
          : `<slot></slot>`
        }
      </span>
    `;
    
    // Add event listener to check if slot has content - only if we're using a slot
    if (!dictKey) {
      const slot = this.shadowRoot!.querySelector('slot');
      slot?.addEventListener('slotchange', this.handleSlotChange);
    }
  }

  /**
   * Handle slot content changes
   */
  private handleSlotChange = (event: Event) => {
    const slot = event.target as HTMLSlotElement;
    const assignedNodes = slot.assignedNodes();
    
    // If no content or only whitespace, show fallback text
    if (assignedNodes.length === 0 || 
        (assignedNodes.length === 1 && assignedNodes[0].textContent?.trim() === '')) {
      slot.textContent = this.fallbackText;
    }
  }

  connectedCallback() {
    // Store reference to parent element for positioning calculations
    this.tooltipParent = this.parentNode as HTMLElement;
    
    // Initialize component state based on attributes
    this.updatePlacement();
    this.updateVisibility();
    this.updateArrowVisibility();
    
    // Initial check for empty content - only if we're using a slot
    if (!this.getAttribute('dict-key')) {
      const slot = this.shadowRoot!.querySelector('slot');
      if (slot) {
        const assignedNodes = slot.assignedNodes();
        if (assignedNodes.length === 0 || 
            (assignedNodes.length === 1 && assignedNodes[0].textContent?.trim() === '')) {
          slot.textContent = this.fallbackText;
        }
      }
    }

    // Add window resize listener to adjust tooltip position when window size changes
    window.addEventListener('resize', this.handleWindowResize);
    
    // Add scroll listener to adjust tooltip position when page scrolls
    window.addEventListener('scroll', this.handleScroll, true);
  }

  /**
   * Handle window resize events
   */
  private handleWindowResize = () => {
    if (this.classList.contains('shown')) {
      this.updatePlacement();
    }
  }
  
  /**
   * Handle scroll events
   */
  private handleScroll = () => {
    if (this.classList.contains('shown')) {
      this.updatePlacement();
    }
  }

  /**
   * Handle changes to observed attributes
   */
  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (name === 'show' && oldValue !== newValue) {
      this.updateVisibility();
    }
    if (name === 'placement' && oldValue !== newValue) {
      this.updatePlacement();
    }
    if (name === 'dict-key' && oldValue !== newValue) {
      // Update the as-text element with the new dict-key
      if (newValue) {
        const textSpan = this.shadowRoot!.querySelector('.tooltip-text');
        if (textSpan) {
          const defaultText = this.getAttribute('default-text') ?? '';
          textSpan.innerHTML = `<as-text dict-key="${newValue}">${defaultText}</as-text>`;
        }
      }
    } else if (name === 'default-text' && oldValue !== newValue) {
      // Handle default-text changes
      const dictKey = this.getAttribute('dict-key');
      if (dictKey) {
        const asText = this.shadowRoot!.querySelector('as-text');
        if (asText) {
          asText.textContent = newValue ?? '';
        }
      }
    } else if (name === 'no-arrow' && oldValue !== newValue) {
      this.updateArrowVisibility();
    } else if (name === 'flash' && oldValue !== newValue) {
      if (newValue !== null) {
        // Flash attribute added, trigger the flash
        this.flashTooltip();
      } else {
        // Flash attribute removed, cancel any ongoing flash
        if (this.flashTimeout) {
          clearTimeout(this.flashTimeout);
          this.flashTimeout = undefined;
        }
      }
    }
    // flash-duration and flash-delay don't require immediate action
    // They will be used on the next flash
  }

  /**
   * Update tooltip visibility based on the 'show' attribute
   */
  private updateVisibility(): void {
    if (this.hasAttribute('show')&&this.getAttribute('show') !== 'false') {
      this.classList.remove('hiding');
      this.classList.add('shown');
    } else {
      // Only add hiding class if tooltip was previously shown
      const wasShown = this.classList.contains('shown');
      this.classList.remove('shown');

      if (wasShown) {
        // Add hiding class for fade-out animation
        this.classList.add('hiding');

        // Get fade-out duration
        const fadeOutDuration = parseFloat(
          getComputedStyle(this).getPropertyValue('--time-fade-out-tooltip') || '300'
        );

        // Remove hiding class after animation completes
        setTimeout(() => {
          this.classList.remove('hiding');
          // Reset any inline position adjustments when hiding
          this.style.marginLeft = '';
          this.style.marginTop = '';
        }, fadeOutDuration);
      }
    }
  }

  /**
   * Update tooltip placement based on the 'placement' attribute
   */
  private updatePlacement(): void {
    // Remove all placement classes
    this.classList.remove('top', 'bottom', 'left', 'right', 'top-left', 'top-right', 'bottom-left', 'bottom-right');
    
    // Add the appropriate placement class
    const placement = this.getAttribute('placement') || 'bottom';
    this.classList.add(placement);
    
    // Reset any inline styles that might interfere with placement
    this.style.left = '';
    this.style.right = '';
    this.style.top = '';
    this.style.bottom = '';
    this.style.transform = '';
    
    // For top placement, ensure the tooltip is positioned above the parent
    if (placement.startsWith('top')) {
      this.style.bottom = `calc(100% + var(--offset-tooltip, var(--default-offset-tooltip)))`;
      this.style.top = 'auto';
    }
    
    // For bottom-right and top-right placements, ensure they're left-aligned
    if (placement === 'bottom-right' || placement === 'top-right') {
      this.style.left = '0';
      this.style.transform = 'none';
    }
    
    // For bottom-left and top-left placements, ensure they're right-aligned
    if (placement === 'bottom-left' || placement === 'top-left') {
      this.style.right = '0';
      this.style.left = 'auto';
      this.style.transform = 'none';
    }
    
    // Force a reflow to ensure the CSS changes take effect immediately
    void this.offsetHeight;
  }

  /**
   * Update arrow visibility based on the 'no-arrow' attribute
   */
  private updateArrowVisibility(): void {
    const arrowElement = this.shadowRoot!.querySelector('.tooltip-arrow');
    
    if (this.hasAttribute('no-arrow')) {
      // Remove arrow element if it exists
      if (arrowElement) {
        arrowElement.remove();
      }
    } else if (!arrowElement) {
      // Add arrow element if it doesn't exist
      const newArrow = document.createElement('div');
      newArrow.className = 'tooltip-arrow';
      this.shadowRoot!.prepend(newArrow);
    }
  }

  flash(data:{textKey: string, defaultText: string}) {
    devlog("Flashing tooltip", data);
    this.setAttribute('dict-key', data.textKey);

    this.setAttribute('default-text', data.defaultText);

    this.setAttribute('show', 'true');
    setTimeout(() => {
      this.removeAttribute('show');
    }, parseInt(getComputedStyle(this).getPropertyValue('--time-flash-tooltip') || '1000', 10));
  }

  /**
   * Flash the tooltip using attributes - show it briefly then hide it
   */
  private flashTooltip(): void {
    // Clear any existing flash timeout
    if (this.flashTimeout) {
      clearTimeout(this.flashTimeout);
      this.flashTimeout = undefined;
    }

    // Get flash delay (default 0ms) and duration (default 2000ms)
    const delay = parseInt(this.getAttribute('flash-delay') ?? '0');
    const duration = parseInt(this.getAttribute('flash-duration') ?? '2000');

    // Wait for delay, then show the tooltip
    this.flashTimeout = window.setTimeout(() => {
      // Show the tooltip
      this.setAttribute('show', 'true');
      this.updatePlacement();

      // Hide after duration and remove flash attribute
      this.flashTimeout = window.setTimeout(() => {
        this.removeAttribute('show');

        // Remove the flash attribute to prevent re-triggering
        this.removeAttribute('flash');
        this.flashTimeout = undefined;
      }, duration);
    }, delay);
  }

  disconnectedCallback() {
    // Clean up event listeners
    if (!this.getAttribute('dict-key')) {
      const slot = this.shadowRoot!.querySelector('slot');
      slot?.removeEventListener('slotchange', this.handleSlotChange);
    }

    // Clean up the resize observer
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    // Clean up the mutation observer
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }

    // Clean up flash timeout
    if (this.flashTimeout) {
      clearTimeout(this.flashTimeout);
      this.flashTimeout = undefined;
    }

    // Remove window event listeners
    window.removeEventListener('resize', this.handleWindowResize);
    window.removeEventListener('scroll', this.handleScroll, true);
  }
}

// Check if the element is already defined before registering it
if (!customElements.get('as-tooltip')) {
  customElements.define('as-tooltip', AsTooltip);
} else {
  console.warn("as-tooltip already defined");
}



